import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../data";
import { Dialog } from "../../components/ui";
import { toast } from "../../components/Toast";
import { MedienAnsicht, type MedienKommentar } from "../../components/medien/MedienAnsicht";
import { groesse } from "../../lib/medien";
import type { CDeliverable } from "./data";
import { useT } from "./sprache";
import { useCreatorContext } from "./ui";
import { creatorErrorLine } from "./fehler";
import type { Lang } from "./sprache";

/**
 * Upload-Meldung im Creator-Portal: Tarifgrenzen der Agentur nicht mit Tarifnamen und Upgrade (Creator buchen keinen Tarif),
 * sondern mit dem Hinweis an die Agentur; sonst die übliche Übersetzung aus Creator-Sicht.
 */
export function creatorUploadFehler(e: unknown, lang: Lang): string {
  const hint = (e as { hint?: string } | null)?.hint ?? "";
  if (hint.startsWith("limit:")) {
    return lang === "en" ? "Your agency's storage or video allowance is used up. Please let your agency know."
      : "Speicher oder Videokontingent deiner Agentur sind ausgeschöpft. Bitte gib deiner Agentur Bescheid.";
  }
  return creatorErrorLine(e, lang);
}

/**
 * Content-Ablage im Creator-Portal (0155): eigene, noch nicht eingereichte Dateien eines Beitrags und die Korrektur der
 * Agentur mit Kommentaren direkt an der Datei (Zeitmarke im Video, Punkt im Bild). Der Creator kann antworten; Kommentare
 * der Marke sieht er nur, wenn die Agentur Rückmeldungen der Marke direkt weitergibt (die Datenbank filtert).
 */

export interface CMedia {
  id: string;
  deliverable_id: string | null;
  submission_id: string | null;
  kind: "video" | "image" | "pdf";
  file_name: string;
  size_bytes: number;
  status: string;
  sha256: string | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  uploaded_side: "staff" | "talent";
  uploaded_by: string | null;
  created_at: string;
}
const COLS = "id, deliverable_id, submission_id, kind, file_name, size_bytes, status, sha256, duration_ms, width, height, uploaded_side, uploaded_by, created_at";

/** Eigene fertige Uploads zu einem Beitrag, die noch in keiner Fassung stecken */
export function useEigeneDateien(deliverableId: string) {
  const { userId } = useCreatorContext();
  return useQuery({
    queryKey: ["creator", "media_files", deliverableId, "offen"],
    queryFn: () => api.select<CMedia>("media_files", { columns: COLS, filters: [["deliverable_id", "eq", deliverableId], ["status", "eq", "ready"], ["submission_id", "is", null]],
      order: [["created_at", "asc"]] }).then((r) => r.filter((m) => m.uploaded_side === "talent" && (!userId || m.uploaded_by === userId))),
  });
}

export function EigeneDateienListe({ deliverableId, abgewaehlt, setAbgewaehlt }: { deliverableId: string; abgewaehlt: Set<string>; setAbgewaehlt: (s: Set<string>) => void }) {
  const t = useT();
  const { lang } = useCreatorContext();
  const q = useEigeneDateien(deliverableId);
  const qc = useQueryClient();
  const liste = q.data ?? [];
  if (!liste.length) return null;

  return (
    <fieldset className="stack co-eigene">
      <legend>{t("Hochgeladene Dateien für diesen Entwurf", "Uploaded files for this draft")}</legend>
      {liste.map((m) => (
        <div key={m.id} className="row between">
          <label className="tk-check">
            <input type="checkbox" checked={!abgewaehlt.has(m.id)} onChange={(e) => {
              const n = new Set(abgewaehlt);
              if (e.target.checked) n.delete(m.id);
              else n.add(m.id);
              setAbgewaehlt(n);
            }} />
            <span>{m.file_name} · {groesse(m.size_bytes, lang)}</span>
          </label>
          <button type="button" onClick={() => void api.rpc("media_delete", { p_media: m.id })
            .then(() => qc.invalidateQueries({ queryKey: ["creator", "media_files", deliverableId] }))
            .catch((e: unknown) => toast(creatorErrorLine(e, lang), "alert"))}>{t("Entfernen", "Remove")}</button>
        </div>
      ))}
    </fieldset>
  );
}

/** Fassung mit Dateien und Kommentaren ansehen (Korrektur der Agentur oder eigener Entwurf) */
export function FassungDialog({ beitrag, name, mediaIds, feedback, onClose }: {
  beitrag: CDeliverable; name: string; mediaIds: readonly string[]; feedback: string | null; onClose: () => void;
}) {
  const t = useT();
  const [idx, setIdx] = useState(0);
  const dateien = useQuery({ queryKey: ["creator", "media_files", beitrag.id, "fassung", mediaIds.join(",")], enabled: mediaIds.length > 0,
    queryFn: async () => {
      const r = await api.select<CMedia>("media_files", { columns: COLS, filters: [["id", "in", [...mediaIds]]] });
      return mediaIds.map((id) => r.find((m) => m.id === id)).filter((m): m is CMedia => !!m);
    } });
  const m = dateien.data?.[Math.min(idx, (dateien.data?.length ?? 1) - 1)];
  return (
    <Dialog open wide onClose={onClose} title={name}>
      <div className="stack">
        {feedback && <div className="revision-note">{t("Korrekturwunsch", "Change request")}: {feedback}</div>}
        {(dateien.data?.length ?? 0) > 1 && (
          <div className="co-dateireiter" role="tablist">
            {dateien.data!.map((x, i) => <button key={x.id} type="button" role="tab" aria-selected={i === idx} className={i === idx ? "aktiv" : ""} onClick={() => setIdx(i)}>{i + 1}. {x.file_name}</button>)}
          </div>
        )}
        {dateien.isPending && mediaIds.length > 0 && <p className="muted">{t("Wird geladen …", "Loading …")}</p>}
        {m && <CreatorDatei m={m} />}
        {!mediaIds.length && <p className="muted">{t("Diese Fassung hat keine Datei in der Ablage.", "This version has no file in the library.")}</p>}
      </div>
    </Dialog>
  );
}

function CreatorDatei({ m }: { m: CMedia }) {
  const t = useT();
  const { lang } = useCreatorContext();
  const qc = useQueryClient();
  const url = useQuery({ queryKey: ["medien-url", m.id], staleTime: 10 * 60_000, refetchInterval: 12 * 60_000, queryFn: () => api.medien<{ url: string; poster: string | null }>({ action: "url", media: m.id }) });
  const k = useQuery({ queryKey: ["media_comments", m.id], queryFn: () => api.select<{ id: string; time_ms: number | null; pos_x: number | null; pos_y: number | null; body: string;
    author_side: "staff" | "talent" | "brand"; author_name: string | null; resolved_at: string | null; created_at: string }>("media_comments", { filters: [["media_id", "eq", m.id]], order: [["created_at", "asc"]] }) });
  const kommentare: MedienKommentar[] = (k.data ?? []).map((c) => ({ id: c.id, time_ms: c.time_ms, x: c.pos_x, y: c.pos_y, body: c.body, side: c.author_side,
    name: c.author_name, resolved: !!c.resolved_at, at: c.created_at }));
  return (
    <MedienAnsicht datei={{ id: m.id, name: m.file_name, kind: m.kind, size: m.size_bytes, sha256: m.sha256, duration_ms: m.duration_ms, width: m.width, height: m.height }}
      url={url.data?.url ?? null} poster={url.data?.poster ?? null} kommentare={kommentare} t={t} onLadefehler={() => void url.refetch()}
      seiten={{ staff: t("Agentur", "Agency"), talent: t("Du", "You"), brand: t("Marke", "Brand") }}
      onKommentar={async (c) => {
        try {
          await api.rpc("media_comment_add", { p_media: m.id, p_body: c.body, p_time_ms: c.time_ms, p_x: c.x, p_y: c.y });
          await qc.invalidateQueries({ queryKey: ["media_comments", m.id] });
          return true;
        } catch (e) {
          toast(creatorErrorLine(e, lang), "alert");
          return false;
        }
      }} />
  );
}
